/**
 * User Activity Detection Utility
 * Tracks user activity to determine if they are actively using the application
 * or have been idle for session timeout management
 */

export interface ActivityState {
  isActive: boolean;
  lastActivityTime: number;
  idleDuration: number;
}

class UserActivityTracker {
  private lastActivityTime: number = Date.now();
  private isActive: boolean = true;
  private listeners: Set<(state: ActivityState) => void> = new Set();
  private checkInterval: NodeJS.Timeout | null = null;
  private readonly ACTIVITY_EVENTS = [
    'mousedown',
    'mousemove',
    'keypress',
    'scroll',
    'touchstart',
    'click',
    'focus',
  ];

  constructor() {
    this.setupActivityListeners();
    this.startActivityCheck();
  }

  /**
   * Set up event listeners for user activity
   */
  private setupActivityListeners() {
    const handleActivity = () => {
      this.updateActivity();
    };

    // Add event listeners for various user interactions
    this.ACTIVITY_EVENTS.forEach(event => {
      document.addEventListener(event, handleActivity, true);
    });

    // Handle visibility change (tab switching)
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        this.updateActivity();
      }
    });
  }

  /**
   * Update the last activity time and mark user as active
   */
  private updateActivity() {
    const now = Date.now();
    this.lastActivityTime = now;
    
    if (!this.isActive) {
      this.isActive = true;
      this.notifyListeners();
    }
  }

  /**
   * Start periodic check for idle state
   */
  private startActivityCheck() {
    // Check every 30 seconds
    this.checkInterval = setInterval(() => {
      this.checkIdleState();
    }, 30000);
  }

  /**
   * Check if user has become idle
   */
  private checkIdleState() {
    const now = Date.now();
    const idleDuration = now - this.lastActivityTime;
    
    // Consider user idle after 2 minutes of inactivity
    const idleThreshold = 2 * 60 * 1000; // 2 minutes
    const wasActive = this.isActive;
    this.isActive = idleDuration < idleThreshold;
    
    // Notify listeners if activity state changed
    if (wasActive !== this.isActive) {
      this.notifyListeners();
    }
  }

  /**
   * Notify all listeners of activity state change
   */
  private notifyListeners() {
    const state: ActivityState = {
      isActive: this.isActive,
      lastActivityTime: this.lastActivityTime,
      idleDuration: Date.now() - this.lastActivityTime,
    };
    
    this.listeners.forEach(listener => {
      try {
        listener(state);
      } catch (error) {
        console.error('Error in activity listener:', error);
      }
    });
  }

  /**
   * Get current activity state
   */
  getActivityState(): ActivityState {
    return {
      isActive: this.isActive,
      lastActivityTime: this.lastActivityTime,
      idleDuration: Date.now() - this.lastActivityTime,
    };
  }

  /**
   * Check if user is currently active
   */
  isUserActive(): boolean {
    this.checkIdleState(); // Update state before returning
    return this.isActive;
  }

  /**
   * Get time since last activity in milliseconds
   */
  getIdleDuration(): number {
    return Date.now() - this.lastActivityTime;
  }

  /**
   * Check if user has been active within the specified time window
   * @param timeWindowMs - Time window in milliseconds
   */
  wasActiveWithin(timeWindowMs: number): boolean {
    return this.getIdleDuration() < timeWindowMs;
  }

  /**
   * Subscribe to activity state changes
   * @param listener - Function to call when activity state changes
   * @returns Unsubscribe function
   */
  subscribe(listener: (state: ActivityState) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /**
   * Force mark user as active (useful for programmatic activity)
   */
  markActive() {
    this.updateActivity();
  }

  /**
   * Clean up event listeners and intervals
   */
  destroy() {
    // Remove event listeners
    const handleActivity = () => this.updateActivity();
    this.ACTIVITY_EVENTS.forEach(event => {
      document.removeEventListener(event, handleActivity, true);
    });

    // Clear interval
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }

    // Clear listeners
    this.listeners.clear();
  }
}

// Create singleton instance
const userActivityTracker = new UserActivityTracker();

export default userActivityTracker;

// Export convenience functions
export const isUserActive = () => userActivityTracker.isUserActive();
export const getActivityState = () => userActivityTracker.getActivityState();
export const wasActiveWithin = (timeWindowMs: number) => userActivityTracker.wasActiveWithin(timeWindowMs);
export const subscribeToActivity = (listener: (state: ActivityState) => void) => userActivityTracker.subscribe(listener);
export const markUserActive = () => userActivityTracker.markActive();
